import React, { useRef, useCallback } from "react";
import Editor, { OnMount } from "@monaco-editor/react";
import { Box, CircularProgress } from "@mui/material";

interface MonacoJsonEditorProps {
  value: string;
  onChange: (value: string) => void;
  height?: string | number;
  placeholder?: string;
  readOnly?: boolean;
}

/**
 * Monaco Editor configured for JSON editing with auto-format on type
 */
export const MonacoJsonEditor: React.FC<MonacoJsonEditorProps> = ({
  value,
  onChange,
  height = 400,
  readOnly = false,
}) => {
  const editorRef = useRef<any>(null);

  const handleChange = useCallback(
    (newValue: string | undefined) => {
      onChange(newValue || "");
    },
    [onChange]
  );

  // Set up editor with formatting
  const handleEditorMount: OnMount = (editor, monaco) => {
    editorRef.current = editor;

    // Set up format on type
    editor.getModel()?.updateOptions({
      tabSize: 2,
      insertSpaces: true,
    });

    // Add keyboard shortcut for format (Shift+Alt+F or Cmd+Shift+F)
    editor.addAction({
      id: "format-json",
      label: "Format JSON",
      keybindings: [
        monaco.KeyMod.Shift | monaco.KeyMod.Alt | monaco.KeyCode.KeyF,
      ],
      run: () => {
        editor.getAction("editor.action.formatDocument")?.run();
      },
    });
  };

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        overflow: "hidden",
      }}
    >
      <Editor
        height={height}
        defaultLanguage="json"
        value={value}
        onChange={handleChange}
        onMount={handleEditorMount}
        loading={
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height={height}
          >
            <CircularProgress size={24} />
          </Box>
        }
        options={{
          minimap: { enabled: false },
          lineNumbers: "on",
          fontSize: 13,
          fontFamily: "Monaco, Menlo, 'Ubuntu Mono', Consolas, monospace",
          tabSize: 2,
          insertSpaces: true,
          formatOnType: true,
          formatOnPaste: true,
          autoIndent: "full",
          scrollBeyondLastLine: false,
          wordWrap: "on",
          readOnly,
          automaticLayout: true,
          folding: true,
          bracketPairColorization: { enabled: true },
          renderValidationDecorations: "on",
        }}
        theme="vs"
      />
    </Box>
  );
};
